import { Injectable } from '@angular/core';
import { BarcodeScanner, BarcodeFormat, GoogleBarcodeScannerModuleInstallState } from '@capacitor-mlkit/barcode-scanning';
import { LocaLstorageService } from 'src/app/store/localstorage.service';
import { HelperProvider } from 'src/app/providers/helper/helper';


/**
 * @description BarcodeScanProvider helps to scan qr codes with scan method.
 * Uses @capacitor-mlkit/barcode-scanning which provides a native scanner UI
 * with built-in torch toggle, so the lightUpQR setting from localStorage
 * is handled by the user via the native scanner interface.
 */
@Injectable()
export class BarcodeScanProvider {
  constructor(
    private localStorageService: LocaLstorageService,
    private helper: HelperProvider,
  ) {}

  /**
   * Ensures the Google Barcode Scanner module is installed.
   * Returns true if the module is available and scanning can proceed.
   * Returns false if the module was just installed (user should tap scan again).
   * Shows alert on installation failure — never throws.
   */
  private async ensureModuleAvailable(): Promise<boolean> {
    const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
    if (available) {
      return true;
    }

    const agreed = await new Promise<boolean>(resolve => {
      this.helper.confirm(
        () => resolve(true),
        'Google Barcode Scanner module is required to scan QR codes. Install it now?',
        'Install Required',
        () => resolve(false),
        'Install',
      );
    });

    if (!agreed) {
      return false;
    }

    await this.helper.showLoading('Preparing installation…');
    await BarcodeScanner.installGoogleBarcodeScannerModule();

    return new Promise<boolean>(resolve => {
      BarcodeScanner.addListener('googleBarcodeScannerModuleInstallProgress', async event => {
        switch (event.state) {
          case GoogleBarcodeScannerModuleInstallState.PENDING:
            await this.helper.showLoading('Preparing installation…');
            break;
          case GoogleBarcodeScannerModuleInstallState.DOWNLOADING:
            await this.helper.showLoading(`Downloading… ${event.progress ?? 0}%`);
            break;
          case GoogleBarcodeScannerModuleInstallState.INSTALLING:
            await this.helper.showLoading('Installing…');
            break;
          case GoogleBarcodeScannerModuleInstallState.DOWNLOAD_PAUSED:
            await this.helper.showLoading('Download paused…');
            break;
          case GoogleBarcodeScannerModuleInstallState.COMPLETED:
            BarcodeScanner.removeAllListeners();
            this.helper.hideLoading();
            await this.helper.showToast('Module installed. Please tap scan again.');
            resolve(false);
            break;
          case GoogleBarcodeScannerModuleInstallState.CANCELED:
            BarcodeScanner.removeAllListeners();
            this.helper.hideLoading();
            await this.helper.alert('Module installation was canceled.');
            resolve(false);
            break;
          case GoogleBarcodeScannerModuleInstallState.FAILED:
            BarcodeScanner.removeAllListeners();
            this.helper.hideLoading();
            await this.helper.alert('Module installation failed. Please try again.');
            resolve(false);
            break;
          case GoogleBarcodeScannerModuleInstallState.UNKNOWN:
          default:
            BarcodeScanner.removeAllListeners();
            this.helper.hideLoading();
            await this.helper.alert('Module installation failed with an unknown error.');
            resolve(false);
            break;
        }
      });
    });
  }

  /**
   * return Promise<string> with result of qr scanning
   * 1) success: result.length > 0 - normal case
   * 2) success: but result.length === 0, this is wrong qr or qr scanning canceled
   * 3) error: str with qr scanning error
   */
  async scan(): Promise<string> {
    const moduleReady = await this.ensureModuleAvailable();
    if (!moduleReady) {
      return Promise.reject('canceled');
    }

    const { supported } = await BarcodeScanner.isSupported();
    if (!supported) {
      throw new Error('Barcode scanning is not supported on this device');
    }

    const { camera } = await BarcodeScanner.checkPermissions();
    if (camera !== 'granted') {
      const result = await BarcodeScanner.requestPermissions();
      if (result.camera !== 'granted') {
        throw new Error('Camera permission is required to scan QR codes');
      }
    }

    const { barcodes } = await BarcodeScanner.scan({
      formats: [BarcodeFormat.QrCode],
    });

    if (barcodes.length > 0) {
      return barcodes[0].rawValue;
    }

    return Promise.reject('canceled');
  }
}
